import {
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import { red } from "@mui/material/colors";
import { cloneDeep, isEqual } from "lodash";
import { ChangeEvent, FC, forwardRef, useContext, useState } from "react";
import {
  Controller,
  FieldValues,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { IMaskInput } from "react-imask";
import {
  AppContextType,
  StateContext,
  StateContextType,
} from "../../context/ApplicationContext";
import { funcExecutor } from "../../functions/generic-functions";
import { evaluateExpression } from "../../functions/parser";
import {
  ExtendedEvents,
  FunctionDefinition,
  NameValueTuple,
} from "../model/form-configuration";
import HelperText from "./HelperText";
import { PPTextFieldProps } from "./PPField";

let fontColor: string = "text.secondary";
const fieldIsRequired: string = "Field is required";
const invalidEmailFormat: string = "Invalid email format";

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

// Matches any non-whitespace characters
const EmailMask = /^\S*@?\S*$/;

const TextMaskEmail = forwardRef<HTMLElement, CustomProps>((props, ref) => {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask={EmailMask}
      inputRef={ref as any}
      onAccept={(value: any) =>
        onChange({ target: { name: props.name, value } })
      }
    />
  );
});

TextMaskEmail.displayName = "TextMaskEmail";

interface State {
  textmask: string;
}

const Email: FC<PPTextFieldProps> = (props) => {
  const { control, getValues, setValue } = useFormContext();
  const stateCtx: StateContextType = useContext(StateContext);

  const requiredTokens: string[] = props.extendedAttributes["required"]
    ? props.extendedAttributes["required"].split(" ")
    : [];
  const watch: FieldValues = useWatch({ control, defaultValue: undefined });
  const isRequired: boolean = evaluateExpression(requiredTokens, watch);

  const appContext = stateCtx.applicationContext;
  const setAppContext = stateCtx.setApplicationContext;

  const [errorMessage, setErrorMessage]: [
    string,
    React.Dispatch<React.SetStateAction<string>>
  ] = useState(fieldIsRequired);

  const getString = (nameValue: NameValueTuple): string => nameValue.value;

  const getExpression = (nameValue: NameValueTuple): string => nameValue.value;

  const getStringArray = (nameValue: NameValueTuple): string[] => {
    const stringArrRegEx: RegExp = /\[(.*?)\]/;
    const stringVals: RegExpMatchArray | null =
      nameValue.value.match(stringArrRegEx);
    if (stringVals !== null) {
      return stringVals[1].split(",");
    } else return [] as string[];
  };

  const getParameter = (nameValue: NameValueTuple): any => {
    let param: any = undefined;
    switch (nameValue.type) {
      case "string":
        param = getString(nameValue);
        break;
      case "stringArray":
        param = getStringArray(nameValue);
        break;
      case "rpn":
        param = getExpression(nameValue);
        break;
      default:
        param = nameValue.value;
    }
    return param;
  };

  const onChange = (appContext: AppContextType) => {
    const events: ExtendedEvents | undefined = props.extendedEvents;

    if (events?.onChange) {
      const functions: FunctionDefinition[] = events.onChange;
      const sortedFunctions: FunctionDefinition[] = functions
        .slice()
        .sort(
          (a: FunctionDefinition, b: FunctionDefinition) => a.order - b.order
        );

      sortedFunctions.forEach((func: FunctionDefinition) => {
        const sortedParams: NameValueTuple[] = func.parameters
          .slice()
          .sort((a: NameValueTuple, b: NameValueTuple) => a.order - b.order);

        funcExecutor(
          func.name,
          sortedParams.map((nameValue: NameValueTuple): any =>
            getParameter(nameValue)
          ),
          getValues,
          setValue,
          appContext
        );
      });
    }
  };

  return (
    <Controller
      render={({ field }) => {
        const isFieldInvalid: boolean =
          appContext.fieldState[field.name] !== undefined &&
          !appContext.fieldState[field.name];
        const isHideField: boolean =
          appContext.fieldHide[field.name] !== undefined &&
          appContext.fieldHide[field.name];
        const isDisabledField: boolean =
          appContext.fieldDisable[field.name] !== undefined &&
          appContext.fieldDisable[field.name];
        return (
          <FormControl error={isFieldInvalid} fullWidth>
            <TextField
              {...field}
              sx={isHideField ? { display: "none" } : null}
              InputProps={{
                name: "textmask",
                inputComponent: TextMaskEmail as any,
                inputProps: {
                  component: IMaskInput,
                  maxLength: props.length,
                },
                value: watch[props.id],
                onChange: (event: ChangeEvent<HTMLInputElement>) => {
                  field.onChange(event.target.value);

                  //https://www.regular-expressions.info/email.html
                  const emailRegEx =
                    /^(?=[A-Z0-9][A-Z0-9@._%+-]{5,253}$)[A-Z0-9._%+-]{1,64}@(?:(?=[A-Z0-9-]{1,63}\.)[A-Z0-9]+(?:-[A-Z0-9]+)*\.){1,8}[A-Z]{2,63}$/;
                  let newAppContext: AppContextType = cloneDeep(appContext);
                  const isValidFormat: boolean =
                    event.target.value.length == 0 ||
                    emailRegEx.test(event.target.value.toUpperCase());
                  newAppContext.fieldState[field.name] =
                    (isRequired &&
                      event.target.value.length > 0 &&
                      isValidFormat) ||
                    (!isRequired &&
                      (isValidFormat || event.target.value.length == 0));
                  setErrorMessage(
                    isValidFormat ? fieldIsRequired : invalidEmailFormat
                  );

                  onChange(newAppContext);
                  if (!isEqual(appContext, newAppContext))
                    setAppContext(newAppContext);

                  return event.target.value;
                },
              }}
              variant="outlined"
              id={props.id}
              label={
                <Typography
                  sx={{ color: isFieldInvalid ? red.A700 : fontColor }}
                >
                  {props.label + (isRequired ? " *" : "")}
                </Typography>
              }
              fullWidth
            />
            <FormHelperText sx={isHideField ? { display: "none" } : null}>
              {isFieldInvalid ? (
                errorMessage
              ) : (
                <HelperText helperText={props.helperText}></HelperText>
              )}
            </FormHelperText>
          </FormControl>
        );
      }}
      name={props.id}
      control={control}
    />
  );
};

export default Email;
//Email.whyDidYouRender = true;
